/**
 * Verdict → colour, and verdict → word, for the two dashboard surfaces that show a
 * project's health as a severity dot: the BCT project grid and the customer dashboard.
 *
 * It is not the only mapping of `ProjectHealth` in the frontend, and that is deliberate.
 * `@/components/projects/ProjectHealthBadge` carries its own `LABEL_KEY` and `TONE` for
 * the projects list and the project detail page, where the verdict is a filled badge
 * rather than a dot — and it disagrees on purpose: `ON_TRACK` is green there and neutral
 * here, because a badge that says nothing is invisible while a dot that says nothing is
 * the point. Folding that surface in would be a design change, not a cleanup, so it is
 * its own decision and not this table's business.
 *
 * s6-1 gave the BCT project grid this map and s6-2 gave the customer dashboard an
 * identical copy, each next to the component using it, because widening `severity.ts`
 * would have reached every dashboard consumer at a moment when the grid's new verdict
 * had not been proven yet. Both copies carried a comment promising this file. Two
 * tables that must agree and nothing that makes them agree is the shape of a bug that
 * shows up as one screen calling a project healthy while the other calls it at risk.
 *
 * It lives next to `./severity.ts` rather than inside it on purpose: that module is the
 * dashboard's colour *model* — which severities exist and what they look like — and the
 * project verdict is one consumer of it. Adding a fifth severity is a change to that
 * module; adding a fifth verdict is a change to this one.
 *
 * Exhaustive over `ProjectHealth` as the frontend declares it — a hand-kept copy of the
 * Prisma enum in `@/components/projects/ProjectHealthBadge`, not the generated type. A
 * fifth verdict added to `prisma/schema.prisma` therefore does **not** fail the
 * typecheck here; it arrives as a value neither table knows, and both surfaces render
 * the row without a dot and without a word rather than guessing one. Closing that drift
 * means generating or importing the frontend type, which is its own story.
 */

import type { ProjectHealth } from '@/components/projects/ProjectHealthBadge';
import type { Severity } from './severity';

/**
 * Which of the four verdicts gets colour, and how much.
 *
 * `ON_TRACK` and `NO_SIGNAL` are both neutral, and that is the colour model's rule
 * rather than an oversight: healthy is the absence of colour, and "no signal" is an
 * absence too — nothing is wrong, there is simply nothing to say yet. The two are told
 * apart by their place in the ordering, not by their colour.
 */
export const HEALTH_SEVERITY: Record<ProjectHealth, Severity> = {
  OFF_TRACK: 'critical',
  AT_RISK: 'warning',
  NO_SIGNAL: 'neutral',
  ON_TRACK: 'neutral',
};

/**
 * The verdict's name, in the `projects` namespace. All four keys exist in all seven
 * languages, so a surface that starts showing the word adds no translation.
 *
 * The older keys under `dashboard.customer.projects.*` and `dashboard.bct.projectHealth.*`
 * (`healthy`, `warning`, `atRisk`) belong to the three-value model and are rendered by
 * nothing; removing them from seven locale files is its own task.
 */
export const HEALTH_LABEL_KEY: Record<ProjectHealth, string> = {
  OFF_TRACK: 'health.offTrack',
  AT_RISK: 'health.atRisk',
  NO_SIGNAL: 'health.noSignal',
  ON_TRACK: 'health.onTrack',
};
